import {BASE_URL} from "@/services/api";
import Ls from '@/services/ls'

/**
 * Abonne cet appareil aux notifications du back-office (nouvelles commandes…). À appeler une
 * fois connecté : la clé VAPID et l'enregistrement de l'abonnement demandent la session.
 * Ne fait rien si les notifications ont été refusées ou ne sont pas prises en charge.
 *
 * L'autorisation n'est demandée que si `prompt` est vrai, c'est-à-dire à la suite d'un appui de
 * l'utilisateur : l'iPhone (application installée) l'exige, et les autres navigateurs masquent
 * de plus en plus une demande faite toute seule au chargement. Sans appui, on n'abonne que
 * l'appareil déjà autorisé — voir pushPermissionNeeded() pour proposer le bouton.
 */
export async function registerPush({ prompt = false } = {}) {
    if (!pushSupported()) {
        return;
    }
    if (Notification.permission === 'denied' || !Ls.get('auth.token')) {
        return;
    }

    if (Notification.permission !== 'granted' && !prompt) {
        return;
    }

    const registration = await navigator.serviceWorker.ready;
    if (Notification.permission !== 'granted') {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
            console.warn('Notifications bloquées.');
            return;
        }
    }
    const token = Ls.get('auth.token');
    const response = await fetch(`${BASE_URL}/admin/vapid-public-key`, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
        },
    });
    const vapidPublicKey = (await response.text()).trim();
    // clé absente (plateforme non configurée) ou réponse d'erreur : pas d'abonnement possible
    if (!response.ok || !/^[A-Za-z0-9_-]{80,}$/.test(vapidPublicKey)) {
        console.warn('Clé de notification indisponible.');
        return;
    }
    const convertedVapidKey = urlBase64ToUint8Array(vapidPublicKey);

    let subscription = await registration.pushManager.getSubscription();

    // Abonnement fait avec une autre clé (clés VAPID changées dans le Landlord) : le service
    // de push refuse alors tous les envois. On le remplace, sinon l'appareil ne reçoit plus
    // rien sans que personne ne le voie.
    if (subscription && !sameKey(subscription.options?.applicationServerKey, convertedVapidKey)) {
        await subscription.unsubscribe().catch(() => {});
        subscription = null;
    }

    if (!subscription) {
        subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: convertedVapidKey,
        });
    }

    await window.axios.post(`${BASE_URL}/admin/push-notification/saveSubscription`, {
        endpoint: subscription.endpoint,
        keys: {
            p256dh: subscription.toJSON().keys.p256dh,
            auth: subscription.toJSON().keys.auth,
        },
        contentEncoding: contentEncoding(),
    });
}

function pushSupported() {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** Cet appareil peut recevoir les notifications mais ne les a pas encore autorisées (ni refusées). */
export function pushPermissionNeeded() {
    return pushSupported() && Notification.permission === 'default' && !!Ls.get('auth.token');
}

// Format de chiffrement accepté par le navigateur : aes128gcm (standard, seul accepté par
// l'iPhone), aesgcm pour les anciens. toJSON() ne le donne pas : il était toujours « aesgcm ».
function contentEncoding() {
    const supported = (window.PushManager && PushManager.supportedContentEncodings) || [];
    return supported.includes('aes128gcm') ? 'aes128gcm' : 'aesgcm';
}

// Clé de l'abonnement (ArrayBuffer, absente sur certains navigateurs) = clé actuelle ?
// Inconnue : on garde l'abonnement plutôt que de le recréer à chaque chargement.
function sameKey(subscriptionKey, currentKey) {
    if (!subscriptionKey) return true;
    const a = new Uint8Array(subscriptionKey);
    return a.length === currentKey.length && a.every((byte, i) => byte === currentKey[i]);
}

// helper
function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map(char => char.charCodeAt(0)));
}
